import React from 'react';
import Joyride, { STATUS } from 'react-joyride';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiTarget, FiSearch, FiList, FiSliders, FiCheckCircle } = FiIcons;

const OnboardingTour = ({ run, setRun, mealReady }) => {
  // Custom styled content component to avoid "wall of text"
  const TourStepContent = ({ title, text, icon: Icon, extra }) => (
    <div className="flex flex-col gap-3 text-left">
      <div className="flex items-center gap-3 border-b border-gray-100 pb-2 mb-1">
        {Icon && <div className="p-2 bg-indigo-50 rounded-full text-indigo-600"><SafeIcon icon={Icon} className="text-xl" /></div>}
        <h3 className="font-bold text-lg text-gray-800 m-0">{title}</h3>
      </div>
      <p className="text-gray-600 text-sm leading-relaxed m-0">{text}</p>
      {extra && <div className="mt-2 text-xs text-indigo-600 font-medium bg-indigo-50 p-2 rounded">{extra}</div>}
    </div>
  );

  const steps = [
    {
      target: 'body',
      content: (
        <TourStepContent 
          title={mealReady ? "Your Meal is Ready!" : "Welcome to CreateMyMeal"}
          text={mealReady 
            ? "We've generated a personalized meal plan based on your profile! Let's take a quick tour to show you how to customize it." 
            : "Let's create your perfect meal in just 3 simple steps. We'll optimize serving sizes to hit your exact nutrition goals."}
          icon={FiCheckCircle}
        />
      ),
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '#food-selector-search',
      content: (
        <TourStepContent 
          title="1. Select Foods"
          text="Search our database or add your own custom foods. Mix and match proteins, carbs, and fats."
          icon={FiSearch}
          extra="Tip: You can select multiple foods at once!"
        />
      ),
    },
    {
      target: '#selected-foods-list',
      content: (
        <TourStepContent 
          title="Set Serving Ranges"
          text="For each food, define the minimum and maximum amount you'd be willing to eat."
          icon={FiList}
          extra="We only optimize within these limits."
        />
      ),
    },
    {
      target: '#macro-targets-panel',
      content: (
        <TourStepContent 
          title="2. Set Targets"
          text="Tell us your calorie and macro goals for this meal."
          icon={FiTarget}
        />
      ),
    },
    {
      target: '#advanced-mode-toggle',
      content: (
        <TourStepContent 
          title="Precision Control"
          text="Switch to Advanced Mode to set specific accuracy thresholds for Protein, Carbs, and Fat."
          icon={FiSliders}
        />
      ),
    },
    {
      target: '#optimize-meal-button',
      content: (
        <TourStepContent 
          title="3. Optimize"
          text="Click here and let our algorithm calculate the perfect portion sizes for you!"
          icon={FiCheckCircle}
        />
      ),
    },
    {
      target: '#meal-results-panel',
      content: (
        <TourStepContent 
          title="Your Results"
          text="Your optimized meal plan will appear here. You can make manual adjustments afterwards."
          icon={FiList}
        />
      ),
    },
  ];

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRun(false);
      localStorage.setItem('meal_calculator_tour_completed', 'true');
    }
  };

  return (
    <Joyride
      steps={steps}
      run={run}
      callback={handleJoyrideCallback}
      continuous
      showProgress
      showSkipButton
      scrollOffset={100}
      disableOverlayClose={true}
      styles={{
        options: {
          zIndex: 10000,
          primaryColor: '#4f46e5',
          textColor: '#374151',
          arrowColor: '#ffffff',
          overlayColor: 'rgba(0, 0, 0, 0.6)',
        },
        tooltip: {
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        },
        tooltipContainer: {
          textAlign: 'left',
        },
        buttonNext: {
          backgroundColor: '#4f46e5',
          borderRadius: '8px',
          padding: '10px 20px',
          fontWeight: 600,
          fontSize: '14px',
        },
        buttonBack: {
          color: '#6b7280',
          marginRight: '15px',
        },
        buttonSkip: {
          color: '#9ca3af',
        }
      }}
    />
  );
};

export default OnboardingTour;